// src/components/RecuperarClave.js
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "../api/axios";

const SIN_ERROR = { message: "", code: "" };
const COOLDOWN_REENVIO = 60; // segundos

const RecuperarClave = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [email, setEmail] = useState("");
  const [digitos, setDigitos] = useState(Array(6).fill(""));
  const [clave, setClave] = useState("");
  const [confirmClave, setConfirmClave] = useState("");
  const [error, setError] = useState(SIN_ERROR);
  const [info, setInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const inputsRef = useRef([]);
  const codigo = digitos.join("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [cooldown, setCooldown] = useState(0);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const emailNormalizado = email.trim().toLowerCase();

  // ================= HELPERS =================
  const validarEmail = (valor) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor);

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    });
  };

  const limpiarMensajes = () => {
    setError(SIN_ERROR);
    setInfo("");
  };

  const manejarError = (err, mensajePorDefecto) => {
    if (err.response) {
      setError({
        message:
          err.response.data?.message ||
          err.response.data?.error ||
          mensajePorDefecto,
        code: err.response.data?.code || "",
      });
    } else {
      setError({ message: "No se pudo conectar con el servidor", code: "" });
    }
  };

  const irAPaso = (nuevoPaso) => {
    limpiarMensajes();
    setStep(nuevoPaso);
  };

  // ================= EFECTOS =================
  // Enfoca el primer dígito al llegar al paso 2
  useEffect(() => {
    if (step === 2) inputsRef.current[0]?.focus();
  }, [step]);

  // Cuenta regresiva del botón "Reenviar código"
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Cuenta regresiva de la redirección tras el éxito (se limpia al desmontar)
  useEffect(() => {
    if (!showSuccess) return;
    if (countdown <= 0) {
      navigate("/login");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showSuccess, countdown, navigate]);

  // =========================
  // 1️⃣ SOLICITAR CÓDIGO
  // =========================
  const enviarCodigo = async (e) => {
    e?.preventDefault();
    limpiarMensajes();

    if (!email.trim()) {
      setError({ message: "Por favor ingrese su correo electrónico", code: "" });
      return;
    }
    if (!validarEmail(emailNormalizado)) {
      setError({ message: "Ingrese un correo electrónico válido", code: "" });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post("/auth/solicitar-codigo-recuperacion", {
        email: emailNormalizado,
      });
      setDigitos(Array(6).fill(""));
      setCooldown(COOLDOWN_REENVIO);
      setStep(2);
    } catch (err) {
      manejarError(err, "Error al enviar el código");
    } finally {
      setIsLoading(false);
    }
  };

  // =========================
  // 🔁 REENVIAR CÓDIGO (usa el endpoint de recuperación)
  // =========================
  const reenviarCodigo = async () => {
    if (cooldown > 0) return;
    limpiarMensajes();
    setIsLoading(true);
    try {
      await axios.post("/auth/solicitar-codigo-recuperacion", {
        email: emailNormalizado,
      });
      setDigitos(Array(6).fill(""));
      inputsRef.current[0]?.focus();
      setCooldown(COOLDOWN_REENVIO);
      setInfo("Le enviamos un nuevo código a su correo");
    } catch (err) {
      manejarError(err, "No se pudo reenviar el código");
    } finally {
      setIsLoading(false);
    }
  };

  // =========================
  // 2️⃣ VERIFICAR CÓDIGO
  // =========================
  const verificarCodigo = async (e) => {
    e?.preventDefault();
    limpiarMensajes();

    if (codigo.length !== 6) {
      setError({ message: "Ingrese el código de 6 dígitos", code: "" });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post("/auth/verificar-codigo", {
        email: emailNormalizado,
        codigo,
      });
      setStep(3);
    } catch (err) {
      manejarError(err, "Código incorrecto");
    } finally {
      setIsLoading(false);
    }
  };

  // =========================
  // 3️⃣ CAMBIAR CONTRASEÑA
  // =========================
  const cambiarClave = async (e) => {
    e?.preventDefault();
    limpiarMensajes();

    if (!Object.values(passwordStrength).every(Boolean)) {
      setError({
        message: "La contraseña no cumple con todos los requisitos de seguridad",
        code: "",
      });
      return;
    }
    if (clave !== confirmClave) {
      setError({ message: "Las contraseñas no coinciden", code: "" });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post("/auth/crear-password", {
        email: emailNormalizado,
        password: clave,
        codigo,
      });
      setCountdown(10);
      setShowSuccess(true);
    } catch (err) {
      manejarError(err, "Error al cambiar la contraseña");
    } finally {
      setIsLoading(false);
    }
  };

  // ================= INPUTS DEL CÓDIGO =================
  const handleDigitoChange = (index, valorCrudo) => {
    const valor = valorCrudo.replace(/\D/g, "").slice(-1);
    const nuevos = [...digitos];
    nuevos[index] = valor;
    setDigitos(nuevos);
    setError(SIN_ERROR);
    if (valor && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleDigitoKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digitos[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pegado = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pegado) return;
    const nuevos = Array(6).fill("");
    pegado.split("").forEach((c, i) => (nuevos[i] = c));
    setDigitos(nuevos);
    setError(SIN_ERROR);
    inputsRef.current[Math.min(pegado.length, 5)]?.focus();
  };

  // ================= RENDER =================
  const claveNoCoincide = confirmClave && clave !== confirmClave;

  return (
    <div className="vh-100 d-flex justify-content-center align-items-center bg-light">
      <div className="card shadow-lg p-4" style={{ maxWidth: "420px", width: "100%" }}>
        <h3 className="text-center text-success mb-4">
          Recuperar Clave Socios ADUTEM
        </h3>

        {/* PASO 1 */}
        {step === 1 && (
          <form onSubmit={enviarCodigo} noValidate>
            <div className="mb-3">
              <label className="form-label">Correo electrónico</label>
              <input
                type="email"
                className={`form-control ${error.message ? "is-invalid" : ""}`}
                placeholder="Ingrese su correo"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(SIN_ERROR);
                }}
                autoFocus
              />
            </div>
            <button type="submit" className="btn btn-success w-100" disabled={isLoading}>
              {isLoading ? "Enviando..." : "Siguiente"}
            </button>
          </form>
        )}

        {/* PASO 2 */}
        {step === 2 && (
          <form onSubmit={verificarCodigo}>
            <h5 className="text-center fw-bold text-success mb-2">Verificación de Código</h5>
            <p className="text-center text-dark mb-2">
              Se envió un código de 6 dígitos a <strong>{emailNormalizado}</strong>
            </p>
            <p className="text-center mb-3">
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => irAPaso(1)}
              >
                ¿Correo equivocado? Cambiar
              </button>
            </p>
            <p className="text-center text-danger fw-semibold mb-3">
              El código expirará en 10 minutos
            </p>

            <div className="d-flex justify-content-between mb-3">
              {digitos.map((d, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className={`form-control text-center fs-4 ${error.message ? "is-invalid" : ""}`}
                  style={{ width: "45px", height: "50px" }}
                  value={d}
                  onPaste={handlePaste}
                  onChange={(e) => handleDigitoChange(index, e.target.value)}
                  onKeyDown={(e) => handleDigitoKeyDown(index, e)}
                />
              ))}
            </div>

            <button type="submit" className="btn btn-success w-100 mb-2" disabled={isLoading}>
              {isLoading ? "Validando..." : "Validar Código"}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={reenviarCodigo}
              disabled={isLoading || cooldown > 0}
            >
              {cooldown > 0 ? `Reenviar código en ${cooldown}s` : "Reenviar código"}
            </button>
          </form>
        )}

        {/* PASO 3 */}
        {step === 3 && (
          <form onSubmit={cambiarClave}>
            <div className="mb-3">
              <label className="form-label">Nueva Contraseña</label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Ingrese su nueva contraseña"
                  value={clave}
                  onChange={(e) => {
                    setClave(e.target.value);
                    checkPasswordStrength(e.target.value);
                    setError(SIN_ERROR);
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Confirmar Contraseña</label>
              <div className="input-group has-validation">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-control ${claveNoCoincide ? "is-invalid" : ""}`}
                  placeholder="Confirme su contraseña"
                  value={confirmClave}
                  onChange={(e) => {
                    setConfirmClave(e.target.value);
                    setError(SIN_ERROR);
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <i className={`bi ${showConfirmPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                </button>
                {claveNoCoincide && (
                  <div className="invalid-feedback">Las contraseñas no coinciden</div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <p className="form-label mb-2 fw-semibold text-success">Requisitos de contraseña:</p>
              <ul className="list-unstyled small mb-0">
                {[
                  ["length", "Mínimo 8 caracteres"],
                  ["uppercase", "Al menos una mayúscula (A-Z)"],
                  ["lowercase", "Al menos una minúscula (a-z)"],
                  ["number", "Al menos un número (0-9)"],
                  ["special", "Al menos un carácter especial (!@#$% etc.)"],
                ].map(([clave_, texto]) => (
                  <li
                    key={clave_}
                    className={`d-flex align-items-center gap-2 mb-1 ${
                      passwordStrength[clave_] ? "text-success" : "text-muted"
                    }`}
                  >
                    {passwordStrength[clave_] ? "✓" : "○"} {texto}
                  </li>
                ))}
              </ul>
            </div>

            <button type="submit" className="btn btn-success w-100" disabled={isLoading}>
              {isLoading ? "Procesando..." : "Cambiar Contraseña"}
            </button>
          </form>
        )}

        {/* Mensaje informativo */}
        {info && <div className="alert alert-success mt-3 mb-0">{info}</div>}

        {/* Error + acciones según el código del backend */}
        {error.message && (
          <div className="alert alert-danger mt-3 mb-0">
            <div>{error.message}</div>

            {error.code === "CUENTA_PENDIENTE" && (
              <Link to="/registro" className="alert-link d-inline-block mt-2">
                Ir a crear cuenta
              </Link>
            )}

            {error.code === "CORREO_NO_REGISTRADO" && (
              <Link to="/registro" className="alert-link d-inline-block mt-2">
                Ir a crear cuenta
              </Link>
            )}

            {error.code === "CUENTA_INACTIVA" && (
              <div className="small mt-2">Comuníquese con administración para reactivarla.</div>
            )}

            {error.code === "CODIGO_EXPIRADO" && step === 2 && (
              <div className="small mt-2">Use el botón "Reenviar código" para recibir uno nuevo.</div>
            )}

            {step === 3 &&
              (error.code === "CODIGO_EXPIRADO" || error.code === "CODIGO_INVALIDO") && (
                <button
                  type="button"
                  className="btn btn-link alert-link p-0 d-block mt-2"
                  onClick={() => irAPaso(2)}
                >
                  Volver a ingresar el código
                </button>
              )}
          </div>
        )}

        <div className="text-center mt-3">
          <small>
            <Link to="/login" className="text-decoration-none text-success">
              Volver al inicio de sesión
            </Link>
          </small>
        </div>
      </div>

      {/* Overlay de carga */}
      {isLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}
        >
          <div className="bg-white p-4 rounded shadow text-center">
            <div className="spinner-border text-success mb-3"></div>
            <p className="fw-semibold mb-0">Procesando solicitud…</p>
          </div>
        </div>
      )}

      {/* Éxito */}
      {showSuccess && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10000 }}
        >
          <div className="bg-white p-4 rounded shadow text-center">
            <h5 className="text-success fw-bold mb-3">¡Éxito!</h5>
            <p>Tu contraseña ha sido cambiada correctamente.</p>
            <p className="text-muted">
              Serás redirigido en <strong>{countdown}</strong> segundos.
            </p>
            <button className="btn btn-success w-100" onClick={() => navigate("/login")}>
              Ir al Login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecuperarClave;